import {
  type PolicyAction,
  type PolicySubject,
  UserAbility,
  viewPermissionUserGroupSchema,
  viewPermissionUserSchema,
} from '@salescore/features'
import { isNotNullish } from 'effect/Predicate'

import type { Me } from './useMe'
import { useMe } from './useMe'

export const useCan = (action: PolicyAction, subject?: PolicySubject): boolean => {
  const me = useMe()
  return can(me, action, subject)
}

export function can(me: Me, action: PolicyAction, subject?: PolicySubject): boolean {
  const userAbility = new UserAbility(
    {
      id: me.myUser.id,
      role: me.myUser.role === 'guest' ? 'member' : me.myUser.role,
      organizationId: me.organization.id,
      userGroupIds: me.myUser.resourceUser?.user_group_ids ?? [],
    },
    {
      resolve: ({ action, subject }) => {
        const {
          myUser: {
            organization: { policies: orgPolicies },
          },
        } = me
        if (subject === undefined) {
          // NOTE: コード(defaultPolicies.ts)でのポリシー管理から脱却し、
          //       デフォルト/カスタム権限の両方をPolicyテーブルから取得する。
          //       BEで組織紐づかないデフォルト権限用のOR条件(organizationId=null)を加えている
          return {
            policies: orgPolicies
              .filter((x) => x.action === action && (x.subject === undefined || x.subject === null))
              .map((x) => ({ ...x })),
            viewPermissionUsers: [],
            viewPermissionUserGroups: [],
          }
        }

        return {
          policies: orgPolicies
            .filter(
              (x) => x.subject?.id === subject.id && x.subject.entityType === subject.entityType && x.action === action,
            )
            .map((x) => ({ ...x, subject: x.subject ?? undefined })),
          viewPermissionUsers: (me.viewPermissionUsers ?? [])
            .filter((x) => x.action === action)
            .map((x) => viewPermissionUserSchema.safeParse(x))
            .map((x) => (x.success ? x.data : undefined))
            .filter(isNotNullish),
          viewPermissionUserGroups: (me.viewPermissionUserGroups ?? [])
            .filter((x) => x.action === action)
            .map((x) => viewPermissionUserGroupSchema.safeParse(x))
            .map((x) => (x.success ? x.data : undefined))
            .filter(isNotNullish),
        }
      },
    },
  )
  return userAbility.can(action, subject)
}
